import {useCallback, useEffect, useState} from "react";

import {
  requestServiceWorkerUpdate,
  scheduleVersionCheck,
  stopDeployDetectionBurst,
  subscribeImmediateVersionChecks,
} from "@/src/modules/core/lib/app-update-checker";
import {clearReloadGuard, tryAcquireReloadLock} from "@/src/modules/core/lib/app-reload-coordinator";
import {forceDeployReload} from "@/src/modules/core/lib/force-deploy-reload";
import {isStandalonePwa} from "@/src/modules/core/lib/is-standalone-pwa";

const POLL_INTERVAL_MS = 15 * 1000;
const STANDALONE_POLL_INTERVAL_MS = 3 * 1000;
/** Collapse focus + visibility + pageshow + online into one resume check. */
const APP_ACTIVE_DEBOUNCE_MS = 400;

export function useVersionChecker() {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);

  const onNewBuildDetected = useCallback(() => {
    stopDeployDetectionBurst();
    setIsNewVersionAvailable(true);
  }, []);

  const triggerCheck = useCallback(() => {
    scheduleVersionCheck(onNewBuildDetected);
  }, [onNewBuildDetected]);

  const triggerCheckImmediate = useCallback(() => {
    scheduleVersionCheck(onNewBuildDetected, {immediate: true});
  }, [onNewBuildDetected]);

  useEffect(
    () => subscribeImmediateVersionChecks(triggerCheckImmediate, onNewBuildDetected),
    [triggerCheckImmediate, onNewBuildDetected],
  );

  useEffect(() => {
    let pollId: ReturnType<typeof setInterval> | null = null;
    let appActiveTimer: ReturnType<typeof setTimeout> | null = null;
    const pollIntervalMs = isStandalonePwa() ? STANDALONE_POLL_INTERVAL_MS : POLL_INTERVAL_MS;

    const stopPolling = () => {
      if (pollId) {
        clearInterval(pollId);
        pollId = null;
      }
    };

    const startPolling = () => {
      stopPolling();
      pollId = setInterval(triggerCheck, pollIntervalMs);
    };

    const clearAppActiveTimer = () => {
      if (appActiveTimer) {
        clearTimeout(appActiveTimer);
        appActiveTimer = null;
      }
    };

    const runAppActive = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      triggerCheck();
      requestServiceWorkerUpdate();
      startPolling();
    };

    const onAppActive = () => {
      clearAppActiveTimer();
      appActiveTimer = setTimeout(() => {
        appActiveTimer = null;
        runAppActive();
      }, APP_ACTIVE_DEBOUNCE_MS);
    };

    const onAppHidden = () => {
      clearAppActiveTimer();
      stopPolling();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        onAppActive();
      } else {
        onAppHidden();
      }
    };

    if (document.visibilityState === "visible") {
      runAppActive();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onAppActive);
    window.addEventListener("pageshow", onAppActive);
    window.addEventListener("online", onAppActive);

    return () => {
      clearAppActiveTimer();
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onAppActive);
      window.removeEventListener("pageshow", onAppActive);
      window.removeEventListener("online", onAppActive);
    };
  }, [triggerCheck]);

  const reloadPage = useCallback(() => {
    clearReloadGuard();
    if (!tryAcquireReloadLock()) {
      return;
    }
    void forceDeployReload();
  }, []);

  return {isNewVersionAvailable, reloadPage};
}
